import { ArrowTriangleRightIcon } from '@tableau/icons/ArrowTriangleRightIcon';
import { NotificationCheckIcon } from '@tableau/icons/NotificationCheckIcon';
import * as React from 'react';
import { PopupStyle } from '../styles/Standard';
import { generateId } from '../utils/DomUtil';
import { InteractiveDomWrapper } from '../utils/InteractiveDom';
import { HorizontalOriginOption, OriginOptions, VerticalOriginOption } from '../utils/Overlay';
// eslint-disable-next-line import/no-cycle
import { GenericFlyoutListMenuWidget } from './FlyoutListMenuWidget';
import { ListAndMenuPadding } from './List/ListStyles';
import { DisplayValueFormatterProps, IconLibraryIcon } from './ListMenuDisplayFormatters/DisplayValueFormatter';
import { GenericMenuItemInfo } from './ListMenuWidget';

export { DisplayValueFormatterProps };

export interface MenuItemState {
  menuItemActive: boolean; // i.e. showing the submenu
}

export interface MenuItemProps {
  annotation?: string | IconLibraryIcon;
  checked?: boolean;
  detail?: string;
  disabled?: boolean;
  displayFormatterBoundWithData: React.ComponentType<DisplayValueFormatterProps>;
  icon?: string | IconLibraryIcon;
  isGroupLabel?: boolean;
  isHighlighted?: boolean;
  isUnselectable?: boolean;
  itemIndex: number;
  // eslint-disable-next-line deprecation/deprecation
  menuItemInfos?: GenericMenuItemInfo[];
  onHover: (index: number) => void;
  onSelect: (value: string) => void;
  onSubmenuShown: (itemIndex: number) => void;
  requestCloseAll: () => void;
  role?: 'option' | undefined;
  scrollingRootId?: string;
  showIconGutter?: boolean;
  stackingRootId?: string;
  subMenuAllowAutoPosition?: boolean;
  subMenuMaxHeight?: number;
  subMenuShouldAutoPositionOnScrollingRootScroll?: boolean;
  subMenuStyle?: React.CSSProperties;
  testId: string;
  value: string;
  zIndex?: number;
}
export const ActiveMenuItemSubmenuDelayMs = 150; // TFSID 707410
const SubMenuOffset = -(PopupStyle.borderWidth + ListAndMenuPadding);
const SubMenuAnchorOrigin: OriginOptions = { vertical: VerticalOriginOption.top, horizontal: HorizontalOriginOption.right };
const SubMenuStackedOrigin: OriginOptions = { vertical: VerticalOriginOption.top, horizontal: HorizontalOriginOption.left };

const MenuItemStyle: React.CSSProperties = { outline: 'none' };

function shouldShowSubmenu(props: MenuItemProps, state: MenuItemState): boolean {
  return state.menuItemActive
    && !!props.isHighlighted
    && !props.disabled
    && !props.isUnselectable
    && !!props.menuItemInfos;
}

export class MenuItemWidget extends React.PureComponent<MenuItemProps, MenuItemState> {
  private domRef: React.RefObject<HTMLDivElement> = React.createRef();
  private showSubMenuTimeoutId: number;
  private subMenuDomId: string = generateId();

  public state: MenuItemState = {
    menuItemActive: false
  };

  public componentDidMount(): void {
    // use a timeout on initial mount as a proxy for "probably attached to document by now"
    window.setTimeout(() => this.updateFocus());
  }

  public componentDidUpdate(prevProps: MenuItemProps, prevState: MenuItemState): void {
    // If the user hovers over and highlights another menu item while a sub menu is open
    if (prevProps.isHighlighted && !this.props.isHighlighted && this.state.menuItemActive) {
      this.setState({ menuItemActive: false });
    }
    if (!shouldShowSubmenu(prevProps, prevState) && shouldShowSubmenu(this.props, this.state)) {
      this.props.onSubmenuShown(this.props.itemIndex);
    }
    this.updateFocus();
  }

  public componentWillUnmount(): void {
    clearTimeout(this.showSubMenuTimeoutId);
  }

  private hasSubMenu = (): boolean => {
    return !!this.props.menuItemInfos;
  }

  private updateFocus(): void {
    // Put keyboard focus on highlighted item, but not if it has a child submenu open
    if (this.props.isHighlighted && !this.state.menuItemActive && document.body.contains(this.domRef.current)) {
      this.domRef.current!.focus();
    }
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (this.props.disabled) { return; }
    switch (e.key) {
      case ' ':
      case 'Enter':
        this.handleSelect();
        break;
      case 'ArrowRight':
        if (this.hasSubMenu()) {
          this.handleSelect();
        }
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
  }

  // MenuItems with submenus show immediately on button press, but only after a delay on hover
  private handleSelect = (): void => {
    if (this.props.disabled || this.props.isUnselectable) { return; }

    if (this.hasSubMenu()) {
      clearTimeout(this.showSubMenuTimeoutId);
      this.setState({ menuItemActive: true });
    } else {
      this.props.onSelect(this.props.value);
    }
  }

  private handleClick: React.MouseEventHandler = e => {
    e.preventDefault(); // TFS 1079434
    this.handleSelect();
  }

  private handleItemMouseEnter: React.MouseEventHandler = () => {
    if (this.props.disabled || this.props.isUnselectable) { return; }

    this.props.onHover(this.props.itemIndex);
    if (this.hasSubMenu()) {
      this.showSubMenuTimeoutId = window.setTimeout(() => this.setState({ menuItemActive: true }), ActiveMenuItemSubmenuDelayMs);
    }
  }

  private handleItemMouseLeave: React.MouseEventHandler = () => {
    clearTimeout(this.showSubMenuTimeoutId);
  }

  // MenuItems without submenus immediately show active styling on button press
  private handleActive = (active: boolean): void => {
    if (this.hasSubMenu()) {
      return;
    }

    this.setState({ menuItemActive: active });
  }

  // submenu says mouse has entered, so notify containing menu not to dismiss if
  // pointer strayed onto the next item on the way over, TFSID 707410
  private handleSubmenuMouseEnter = () => {
    this.props.onHover(this.props.itemIndex);
  }

  private handleSubmenuClose = (): void => {
    this.setState({ menuItemActive: false });
  }

  private getDisplayElement(): React.ReactElement {
    const { annotation, detail, disabled, icon, isHighlighted } = this.props;
    const active = this.state.menuItemActive && isHighlighted;

    const displayValueFormatterProps: DisplayValueFormatterProps = {
      active,
      annotation: this.hasSubMenu() ? ArrowTriangleRightIcon : annotation,
      detail,
      disabled,
      icon: this.props.checked ? NotificationCheckIcon : icon,
      isGroupLabel: this.props.isGroupLabel,
      isHighlighted,
      showIconGutter: this.props.showIconGutter,
      testId: this.props.testId
    };

    return React.createElement(this.props.displayFormatterBoundWithData, displayValueFormatterProps);
  }

  private getItemClientRect = (): ClientRect | undefined => {
    if (!this.domRef.current) { return undefined; }
    const { height, width, left, right, top, bottom } = this.domRef.current.getBoundingClientRect();
    return { height, width, left, right, top: top + SubMenuOffset, bottom: bottom + SubMenuOffset };
  }

  public render(): React.ReactNode {
    const showSubmenu = shouldShowSubmenu(this.props, this.state);
    const hasSubmenu = this.hasSubMenu();

    if (this.props.isGroupLabel) {
      return this.getDisplayElement();
    }

    return <>
      <InteractiveDomWrapper
        disabled={this.props.disabled}
        handleActive={this.handleActive}
        key='item'
        pointerClick={this.handleClick}
      >
        <div
          aria-checked={this.props.checked}
          aria-disabled={this.props.disabled}
          aria-expanded={showSubmenu ? true : (hasSubmenu ? false : undefined)}
          aria-haspopup={hasSubmenu ? 'menu' : undefined}
          data-tb-test-id={`${this.props.testId}-${this.props.value}-MenuItem`}
          onKeyDown={this.handleKeyDown}
          onMouseEnter={this.handleItemMouseEnter}
          onMouseLeave={this.handleItemMouseLeave}
          ref={this.domRef}
          role={this.props.role || (this.props.checked === undefined ? 'menuitem' : 'menuitemcheckbox')}
          style={MenuItemStyle}
          tabIndex={-1}
        >
          {this.getDisplayElement()}
          {/* eslint-disable-next-line deprecation/deprecation */}
          {showSubmenu && <GenericFlyoutListMenuWidget
            allowAutoPosition={this.props.subMenuAllowAutoPosition}
            anchorOrigin={SubMenuAnchorOrigin}
            getAnchorClientRect={this.getItemClientRect}
            id={this.subMenuDomId}
            maxHeight={this.props.subMenuMaxHeight}
            menuItemInfos={this.props.menuItemInfos!}
            menuStyle={this.props.subMenuStyle}
            onEscapeKeyUp={this.handleSubmenuClose}
            onLeftArrowKeyDown={this.handleSubmenuClose}
            onMouseEnter={this.handleSubmenuMouseEnter}
            onSelect={this.props.onSelect}
            requestCloseAll={this.props.requestCloseAll}
            scrollingRootId={this.props.scrollingRootId}
            shouldAutoPositionOnScrollingRootScroll={this.props.subMenuShouldAutoPositionOnScrollingRootScroll}
            stackedOrigin={SubMenuStackedOrigin}
            stackingRootId={this.props.stackingRootId}
            testId={`${this.props.testId}-${this.props.value}`}
            zIndex={this.props.zIndex}
          />}
        </div>
      </InteractiveDomWrapper>
      {showSubmenu && <div aria-owns={this.subMenuDomId} key='submenu' />}
    </>;
  }
}
